import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["leftPane", "centerPane", "rightPane", "leftHandle", "rightHandle"]
  static values = {
    storageKey: String,
    leftDefault: Number,
    leftMin: Number,
    leftMax: Number,
    rightDefault: Number,
    rightMin: Number,
    rightMax: Number,
    centerMin: Number
  }

  connect() {
    this.leftWidth = this.leftDefault
    this.rightWidth = this.rightDefault

    this.pointerMoveHandler = this.onPointerMove.bind(this)
    this.pointerUpHandler = this.stopDragging.bind(this)
    this.keydownHandler = this.onKeydown.bind(this)
    this.resizeHandler = this.onResize.bind(this)

    this.restoreWidths()
    this.normalizeWidths()
    this.applyWidths()

    this.bindHandle(this.leftHandleTarget)
    this.bindHandle(this.rightHandleTarget)
    window.addEventListener("resize", this.resizeHandler)
  }

  disconnect() {
    this.unbindHandle(this.leftHandleTarget)
    this.unbindHandle(this.rightHandleTarget)
    this.stopDragging()
    window.removeEventListener("resize", this.resizeHandler)
  }

  bindHandle(handle) {
    if (!handle) return

    handle.addEventListener("pointerdown", this.startDragging)
    handle.addEventListener("keydown", this.keydownHandler)
  }

  unbindHandle(handle) {
    if (!handle) return

    handle.removeEventListener("pointerdown", this.startDragging)
    handle.removeEventListener("keydown", this.keydownHandler)
  }

  startDragging = (event) => {
    event.preventDefault()

    this.dragSide = event.currentTarget.dataset.paneResizeSideParam
    this.element.classList.add("pane-resize--dragging")

    window.addEventListener("pointermove", this.pointerMoveHandler)
    window.addEventListener("pointerup", this.pointerUpHandler)
  }

  stopDragging() {
    this.dragSide = null
    this.element.classList.remove("pane-resize--dragging")

    window.removeEventListener("pointermove", this.pointerMoveHandler)
    window.removeEventListener("pointerup", this.pointerUpHandler)
  }

  onPointerMove(event) {
    if (!this.dragSide) return

    const rect = this.element.getBoundingClientRect()
    const centerMin = this.centerMin
    const handleAllowance = 24

    if (this.dragSide === "left") {
      const maxLeft = rect.width - this.rightWidth - centerMin - handleAllowance
      this.leftWidth = this.clamp(
        event.clientX - rect.left,
        this.leftMin,
        Math.min(this.leftMax, maxLeft)
      )
    } else {
      const maxRight = rect.width - this.leftWidth - centerMin - handleAllowance
      this.rightWidth = this.clamp(
        rect.right - event.clientX,
        this.rightMin,
        Math.min(this.rightMax, maxRight)
      )
    }

    this.normalizeWidths()
    this.applyWidths()
    this.persistWidths()
  }

  onKeydown(event) {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return

    event.preventDefault()

    const step = event.key === "ArrowLeft" ? -16 : 16
    const side = event.currentTarget.dataset.paneResizeSideParam

    if (side === "left") {
      this.leftWidth = this.clamp(
        this.leftWidth + step,
        this.leftMin,
        this.leftMax
      )
    } else {
      this.rightWidth = this.clamp(
        this.rightWidth - step,
        this.rightMin,
        this.rightMax
      )
    }

    this.normalizeWidths()
    this.applyWidths()
    this.persistWidths()
  }

  applyWidths() {
    this.element.style.setProperty("--pane-left-width", `${this.leftWidth}px`)
    this.element.style.setProperty("--pane-right-width", `${this.rightWidth}px`)
    this.element.style.setProperty("--pane-center-min", `${this.centerMin}px`)
  }

  restoreWidths() {
    if (!this.hasStorageKeyValue) return

    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKeyValue))
      if (!saved) return

      this.leftWidth = this.clamp(
        saved.left,
        this.leftMin,
        this.leftMax
      )
      this.rightWidth = this.clamp(
        saved.right,
        this.rightMin,
        this.rightMax
      )
    } catch (_error) {
      this.leftWidth = this.leftDefault
      this.rightWidth = this.rightDefault
    }
  }

  persistWidths() {
    if (!this.hasStorageKeyValue) return

    localStorage.setItem(this.storageKeyValue, JSON.stringify({
      left: this.leftWidth,
      right: this.rightWidth
    }))
  }

  clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
  }

  onResize() {
    this.normalizeWidths()
    this.applyWidths()
    this.persistWidths()
  }

  normalizeWidths() {
    const rect = this.element.getBoundingClientRect()
    const availableWidth = rect.width || window.innerWidth
    const handleAllowance = 24
    const maxSideSpace = Math.max(this.leftMin + this.rightMin, availableWidth - this.centerMin - handleAllowance)

    this.leftWidth = this.clamp(this.leftWidth, this.leftMin, this.leftMax)
    this.rightWidth = this.clamp(this.rightWidth, this.rightMin, this.rightMax)

    let totalSideWidth = this.leftWidth + this.rightWidth
    if (totalSideWidth <= maxSideSpace) return

    let overflow = totalSideWidth - maxSideSpace

    const leftShrink = Math.max(0, this.leftWidth - this.leftMin)
    const rightShrink = Math.max(0, this.rightWidth - this.rightMin)
    const totalShrink = leftShrink + rightShrink

    if (totalShrink === 0) return

    const leftReduction = Math.min(leftShrink, Math.round((leftShrink / totalShrink) * overflow))
    this.leftWidth -= leftReduction
    overflow -= leftReduction

    const rightReduction = Math.min(rightShrink, overflow)
    this.rightWidth -= rightReduction
    overflow -= rightReduction

    if (overflow > 0) {
      const extraLeft = Math.min(Math.max(0, this.leftWidth - this.leftMin), overflow)
      this.leftWidth -= extraLeft
      overflow -= extraLeft
    }

    this.leftWidth = this.clamp(this.leftWidth, this.leftMin, this.leftMax)
    this.rightWidth = this.clamp(this.rightWidth, this.rightMin, this.rightMax)
  }

  get leftDefault() {
    return this.leftDefaultValue || 250
  }

  get leftMin() {
    return this.leftMinValue || 190
  }

  get leftMax() {
    return this.leftMaxValue || 360
  }

  get rightDefault() {
    return this.rightDefaultValue || 300
  }

  get rightMin() {
    return this.rightMinValue || 240
  }

  get rightMax() {
    return this.rightMaxValue || 360
  }

  get centerMin() {
    return this.centerMinValue || 680
  }
}
